import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { AdjustmentPanel, FailedPayment, PaymentForm, PaymentFormValues, ReceiptTotalTitle, ReservationDetails } from '../../components';
import { Guests, TripAdjustmentResponse } from 'guest-data-types-ts-lib';
import { AppRoutes } from '../../routes';
import { BillingInformation } from '../../components/BillingInformation/BillingInformation';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from '../../types/store.type';
import { PaymentUtils } from '../../utils';
import ddLogger from '../../logging/ddLogger';
import { TripService } from '../../services/trip/tripService';
import { PaymentConfirmationAdjustmentProps } from '../payment-confirmation/PaymentConfirmationAdjustment';
import { fetchTripError } from '../../store/actions';
import { OccupancyCounts } from '../../types/occupancy';
import segmentService, { Step } from '../../services/segment/segmentService';
import { Currency } from '../../types/preference.type';
import { Spinner } from '../../assets/images';
import * as _ from 'lodash';
export interface ManageGuestConfirmationProps {
    initialOccupancy: OccupancyCounts | null;
    occupancyCounts: OccupancyCounts | null;
    preview: TripAdjustmentResponse | null;
}

export const ManageGuestConfirmation: React.FC = () => {
    const intl = useIntl();
    const history = useHistory();
    const dispatch = useDispatch();
    const [didAdjustmentFailed, setDidAdjustmentFailed] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation<ManageGuestConfirmationProps>();
    const { trip } = useSelector((store: StoreState) => store);
    const { guests, reservation } = trip;

    useEffect(() => {
        if (isOnlyAdjustmentFlow && !_.isNil(guests) && !_.isNil(reservation)) {
            persistAdjustment(reservation.id, preview, guests);
        } else {
            setIsLoading(false);
        }
    }, []);

    if (!location || !location.state || !location.state.preview || !guests || !reservation) {
        history.push(AppRoutes.RESERVATION);
        return null;
    }

    const { initialOccupancy, occupancyCounts, preview } = location.state;
    const { adjustment, detail } = preview;
    const isOnlyAdjustmentFlow =
        adjustment.name === 'updatePetAdjustment' && !_.isNil(detail?.refund?.amount) && detail?.refund?.amount === 0;

    const persistAdjustment = async (
        reservationId: string,
        preview: TripAdjustmentResponse,
        guests: Guests,
        values?: PaymentFormValues
    ) => {
        const { adjustment, detail, uuid } = preview;
        try {
            const persistAdjustmentRequest = PaymentUtils.transformToPersistRequest(preview, guests.primary, detail!.total, values);
            ddLogger.info(`persisting adjustment for ${reservationId}`, { uuid });
            setIsLoading(true);
            const { attributes: response } = (await new TripService().persistAdjustment(reservationId, persistAdjustmentRequest)) as {
                attributes: TripAdjustmentResponse;
            };

            const paymentConfirmationProps: PaymentConfirmationAdjustmentProps = {
                adjustment: adjustment.name,
                preview: preview,
                occupancy_details: { initialOccupancy, occupancyCounts },
            };

            segmentService.trackAdjustmentSuccess(Step.PAYMENT, preview, {});

            ddLogger.info(`adjustment ${adjustment.name} successfully persisted`, {
                delta: response.delta,
                uuid: response.uuid,
            });

            history.push({
                pathname: AppRoutes.PAYMENT_SUCCESSFUL,
                state: paymentConfirmationProps,
            });
        } catch (e) {
            const code = e.code;
            ddLogger.error(`ERROR persisting ${adjustment.name} adjustment for ${reservationId}`, {
                adjustment: adjustment.name,
                error: e.message,
                code,
            });
            segmentService.trackAdjustmentIssue(adjustment as any, code);

            if (e.status === 504) {
                ddLogger.error(`ERROR persisting it is taking longer than expected ${adjustment.name} adjustment for ${reservationId}`, {
                    adjustment: adjustment.name,
                    error: e.message,
                    code,
                });
            }

            if (code) {
                setDidAdjustmentFailed(true);
                return;
            }

            dispatch(fetchTripError(e));
        } finally {
            setIsLoading(false);
        }
    };

    if (isOnlyAdjustmentFlow) {
        return (
            <div className={'loading'}>
                <Spinner />
            </div>
        );
    }

    return (
        <div className={'row'}>
            <div className={'col-12 col-md-7'}>
                <div className={'d-md-none'}>
                    <div className={'type-body-small reservation-details-mobile px-16 py-24'}>
                        <ReservationDetails preview={preview} occupancy_details={{ initialOccupancy, occupancyCounts }} />
                    </div>
                </div>
                {detail!.total <= 0 ? (
                    didAdjustmentFailed ? (
                        <FailedPayment adjustment={adjustment.name} />
                    ) : (
                        <>
                            <BillingInformation
                                isLoading={isLoading}
                                onSubmit={() => {
                                    return persistAdjustment(reservation.id, preview, guests);
                                }}
                                preview={preview}
                            />
                        </>
                    )
                ) : didAdjustmentFailed ? (
                    <FailedPayment adjustment={adjustment.name} />
                ) : (
                    <React.Fragment>
                        <div className="d-none d-md-block p-2">
                            <AdjustmentPanel adjustment={adjustment.name} />
                            <ReceiptTotalTitle
                                summary={{
                                    total: detail!.total,
                                    currency: detail!.currency,
                                }}
                                convertedSummary={
                                    detail!.converted ? { total: detail!.converted.total, currency: detail!.converted.currency } : undefined
                                }
                                locale={intl.locale}
                            />
                        </div>
                        <PaymentForm
                            guests={guests}
                            currency={detail!.currency as Currency}
                            onSubmit={(values) => {
                                return persistAdjustment(reservation.id, preview, guests, values);
                            }}
                        />
                    </React.Fragment>
                )}
            </div>
            <div className={'d-none d-md-block col-md-5'}>
                <ReservationDetails preview={preview} occupancy_details={{ initialOccupancy, occupancyCounts }} />
            </div>
        </div>
    );
};
